// Vorlagen-Bibliothek: ausgewählte Teile (samt Heft-/Korpusgruppe) in ein
// projektunabhängiges JSON serialisieren und wieder einfügen. Engine-frei.
//
// Vorlagen-Format ({ parts: [...] }): keine client_ids und keine material_ids —
// Material steht als Name (projektübergreifendes Matching), interne Verweise
// sind Platzhalter: meta.attached_to = Index im parts-Array, meta.cabinet_id =
// Gruppen-Token ("g0", "g1", …). Positionen x/z relativ zum Gruppenzentrum,
// y bleibt absolut (eingefügte Teile stehen wieder auf gleicher Höhe).
import { newPart, generateClientId } from "./document"

// meta-Felder, die auf eine Korpusgruppe zeigen und deshalb beim Speichern zu
// Platzhaltern werden.
const CABINET_REF_KEYS = [ "cabinet_id", "belongs_to_cabinet" ]

// Das Teil plus transitiv alles, was per meta.attached_to daran hängt, plus
// alle Teile derselben Korpusgruppe(n) — Fixpunkt, damit auch Anhänge von
// Korpusteilen und Korpusgruppen von Anhängen mitkommen.
export function collectGroup(allParts, clientId) {
  const ids = new Set([ clientId ])
  const cabinetIds = new Set()
  let grew = true
  while (grew) {
    grew = false
    for (const part of allParts) {
      if (ids.has(part.client_id)) {
        const cabinetId = part.meta?.cabinet_id
        if (cabinetId && !cabinetIds.has(cabinetId)) { cabinetIds.add(cabinetId); grew = true }
        continue
      }
      const attachedTo = part.meta?.attached_to
      const cabinetId = part.meta?.cabinet_id
      if ((attachedTo && ids.has(attachedTo)) || (cabinetId && cabinetIds.has(cabinetId))) {
        ids.add(part.client_id)
        grew = true
      }
    }
  }
  return allParts.filter((part) => ids.has(part.client_id))
}

// materialsById: Map(id → Material) wie doc.materials, oder plain Object
const materialById = (materials, id) => {
  if (id == null || !materials) return null
  return (materials instanceof Map ? materials.get(id) : materials[id]) ?? null
}

// materialsByName: Map(name → Material) oder plain Object
const materialByName = (materials, name) => {
  if (name == null || !materials) return null
  return (materials instanceof Map ? materials.get(name) : materials[name]) ?? null
}

// Gruppenzentrum in der Ebene: Mitte der Positions-Spannen von x und z
const groupCenter = (parts) => {
  const mid = (values) => Math.round((Math.min(...values) + Math.max(...values)) / 2)
  return { x: mid(parts.map((p) => p.pos_x)), z: mid(parts.map((p) => p.pos_z)) }
}

export function partsToTemplate(parts, materialsById) {
  const indexById = new Map(parts.map((part, index) => [ part.client_id, index ]))
  const center = groupCenter(parts)
  const cabinetTokens = new Map()

  const entries = parts.map((part) => {
    const meta = structuredClone(part.meta ?? {})
    if ("attached_to" in meta) {
      const index = indexById.get(meta.attached_to)
      if (index === undefined) delete meta.attached_to // Anker außerhalb der Gruppe
      else meta.attached_to = index
    }
    // Beide Korpus-Verweise über dieselbe Token-Tabelle: die Zuordnung einer
    // Front zu ihrem Korpus (meta.belongs_to_cabinet) bleibt in der Vorlage
    // erhalten, ohne eine Id aus dem Quellprojekt mitzuschleppen.
    for (const key of CABINET_REF_KEYS) {
      if (!meta[key]) continue
      if (!cabinetTokens.has(meta[key])) cabinetTokens.set(meta[key], `g${cabinetTokens.size}`)
      meta[key] = cabinetTokens.get(meta[key])
    }
    return {
      name: part.name,
      material: materialById(materialsById, part.material_id)?.name ?? null,
      length_mm: part.length_mm, width_mm: part.width_mm, thickness_mm: part.thickness_mm,
      pos_x: part.pos_x - center.x, pos_y: part.pos_y, pos_z: part.pos_z - center.z,
      rot_x: part.rot_x, rot_y: part.rot_y, rot_z: part.rot_z,
      meta
    }
  })

  return { parts: entries }
}

export function templateToParts(data, materialsByName, offset = {}) {
  const entries = data?.parts ?? []
  const parts = entries.map((entry) => newPart({
    name: entry.name,
    material_id: materialByName(materialsByName, entry.material)?.id ?? null,
    length_mm: entry.length_mm, width_mm: entry.width_mm, thickness_mm: entry.thickness_mm,
    pos_x: Math.round(entry.pos_x + (offset.x ?? 0)),
    pos_y: Math.round(entry.pos_y),
    pos_z: Math.round(entry.pos_z + (offset.z ?? 0)),
    rot_x: entry.rot_x, rot_y: entry.rot_y, rot_z: entry.rot_z,
    meta: structuredClone(entry.meta ?? {})
  }))

  // Platzhalter zurück auf echte Ids: attached_to-Index → neue client_id,
  // Gruppen-Token → eine frische Gruppen-Id pro Token
  const cabinetIds = new Map()
  for (const part of parts) {
    const meta = part.meta
    if ("attached_to" in meta) {
      const target = parts[meta.attached_to]
      if (target) meta.attached_to = target.client_id
      else delete meta.attached_to
    }
    for (const key of CABINET_REF_KEYS) {
      if (!meta[key]) continue
      if (!cabinetIds.has(meta[key])) cabinetIds.set(meta[key], generateClientId())
      meta[key] = cabinetIds.get(meta[key])
    }
  }
  return parts
}
